// src/components/Banner.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  IconButton,
  Flex,
  useColorModeValue,
  Divider, // ✅ added
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { fetchActiveBanners } from "../api/api";

function Banner() {
  const [showBanner, setShowBanner] = useState(true);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const textColor = useColorModeValue("gray.800", "gray.100");

  // ✅ Fetch active banners WITH expiry filter + sort
  useEffect(() => {
    const loadBanners = async () => {
      try {
        const res = await fetchActiveBanners();
        const now = new Date();

        if (res.data?.length) {
          const activeSorted = res.data
            .filter(
              (b) => new Date(b.startTime) <= now && new Date(b.endTime) >= now,
            )
            .sort((a, b) => new Date(a.endTime) - new Date(b.endTime));

          setBanners(activeSorted);
        }
      } catch (err) {
        console.error("Error fetching banner:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBanners();
  }, []);

  // Auto-hide after 20 seconds
  useEffect(() => {
    if (banners.length > 0) {
      const timer = setTimeout(() => setShowBanner(false), 20000);
      return () => clearTimeout(timer);
    }
  }, [banners]);

  if (loading || banners.length === 0 || !showBanner) return null;

  return (
    <Box
      position="fixed"
      inset="0"
      bg="rgba(0,0,0,0.7)"
      zIndex="9999"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <Box
        position="relative"
        bgGradient="linear(to-b, #fef6e4, #fde68a)"
        borderRadius="2xl"
        boxShadow="2xl"
        maxW="420px"
        w="90%"
        maxH="80vh"
        overflow="hidden"
      >
        {/* Close */}
        <IconButton
          icon={<CloseIcon boxSize={3} />}
          position="absolute"
          top="10px"
          right="10px"
          size="xs"
          variant="ghost"
          bg="white"
          _hover={{ bg: "orange.100" }}
          borderRadius="full"
          onClick={() => setShowBanner(false)}
          aria-label="Close banner"
          zIndex="10"
        />

        <Box
          p={6}
          overflowY="auto"
          maxH="75vh"
          css={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              background: "#f59e0b",
              borderRadius: "3px",
            },
          }}
        >
          <Flex direction="column" align="center" textAlign="center" gap={4}>
            {/* 🔔 Fixed Title */}
            <Text fontSize="lg" fontWeight="bold" color="orange.700">
              Important Announcement
            </Text>

            {banners.map((b, index) => {
              const parts = b.message.split("|||");
              const header = parts[0]?.trim() || "";
              const subject = parts[1]?.trim() || parts[0]?.trim() || "";
              const footer = parts[2]?.trim() || "";

              return (
                <Box key={b.id} w="100%">
                  {/* HEADER */}
                  {header && (
                    <Text
                      fontSize="md"
                      fontWeight="bold"
                      color="orange.800"
                      mb={2}
                    >
                      {header}
                    </Text>
                  )}

                  {/* SUBJECT */}
                  {subject && (
                    <Text
                      fontSize="md"
                      color={textColor}
                      lineHeight="1.8"
                      whiteSpace="pre-line"
                      fontFamily="'Noto Serif Telugu', serif"
                      mb={3}
                    >
                      {subject}
                    </Text>
                  )}

                  {/* FOOTER */}
                  {footer && (
                    <Text fontSize="sm" color="gray.600">
                      {footer}
                    </Text>
                  )}

                  {/* ✅ Divider between multiple banners */}
                  {index < banners.length - 1 && (
                    <Divider my={4} borderColor="orange.300" />
                  )}
                </Box>
              );
            })}
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}

export default Banner;
